import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { agregarTarea } from "../../../features/registroTareasSlice";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Material UI y Lucide
import { 
  Box, Typography, TextField, Grid, Button, Paper, 
  Stack, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel 
} from "@mui/material";
import { PlusCircle, Printer, Calendar, Layers, Maximize, Hash, DollarSign } from "lucide-react";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = { 
  ice: "#C2EDF0", 
  aqua: "#00C0CA", 
  teal: "#008B9C", 
  dark: "#22404D", 
  slate: "#94A3B8",
  pearl: "#EEF3F4"
};

const Impresion = ({ idPedido }) => {
    // ==========================================
    // LÓGICA ORIGINAL (MANTENIDA)
    // ==========================================
    const tareas = useSelector(state => state.tareas.Tareas);
    const dispatch = useDispatch();
    const [formImpresion, setFormImpresion] = useState({  
        tipoTarea: "Impresion",    
        fechaLimite: "",
        tipo: "",
        cantidadCopias: 0,
        gramaje: "",
        tamanio: "",
        plastificado: false,
        presupuesto: 0,
    });

    const [mostrarFormulario, setMostrarFormulario] = useState(true);

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        let newValue;
        
        if (type === 'radio' && name === 'plastificado') {
            newValue = value === 'true';
        } else if (type === 'number' || name === 'cantidadCopias' || name === 'presupuesto') {           
            newValue = parseInt(value, 10) || 0;
        } else {
            newValue = value;
        }
    
        setFormImpresion({
            ...formImpresion,
            [name]: newValue
        });
    };

    const validar = () => {
        const {fechaLimite, tipo, cantidadCopias, gramaje, tamanio, plastificado, presupuesto } = formImpresion;
        const fechaFormulario = new Date(fechaLimite);
        const fechaActual = new Date();
        fechaActual.setHours(0, 0, 0, 0);
        fechaFormulario.setHours(0, 0, 0, 0);

        if (!tipo) return "Debe seleccionar un tipo de impresión.";
        if (!fechaLimite) return "La fecha límite no puede estar vacía.";
        if (fechaFormulario < fechaActual) return "La fecha límite debe ser una fecha futura.";
        if (cantidadCopias <= 0) return "Debe indicar la cantidad de copias a realizar.";
        if (!gramaje) return "El gramaje no puede estar vacío.";
        if (!tamanio) return "El tamanio no puede estar vacío.";
        if (plastificado === null) return "Se debe informar si lleva plastificado.";
        if (presupuesto === null || isNaN(presupuesto)) return "El presupuesto no puede estar vacío.";
        return ""; 
    };

    const agregarTareaAlPedido = async () => {
        const mensaje = validar();
        if (mensaje !== "") {
            toast.error(mensaje, { position: "bottom-center", autoClose: 3000 });
            return;
        }

        const {fechaLimite, tipo, cantidadCopias, gramaje, tamanio, plastificado, presupuesto }  = formImpresion;
        const registro = {
            id: tareas.length + 1,
            tipoTarea: "Impresion",           
            fechaLimite,
            tipo,
            comentario:"",
            estado: "pendiente",
            cantidadCopias,
            gramaje,
            tamanio,
            plastificado,
            presupuesto,              
        };

        if (idPedido) {   
            const respuesta = await fetch(`https://pideapi2025.azurewebsites.net/api/Tarea/AgregarTareaImpresion?IdPedido=` + idPedido, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'apiKey': localStorage.getItem("apiKey")   
                },
                body: JSON.stringify(registro)
            });

            if (respuesta.ok) {
                setMostrarFormulario(false);
                toast.success('Pedido agregado correctamente', { position: "bottom-center", autoClose: 3000 });
            }
        } else {
            dispatch(agregarTarea(registro));
            setMostrarFormulario(false);
            toast.success('Tarea agregada correctamente', { position: "bottom-center", autoClose: 3000 });
        }
    };
    // ==========================================

    if (!mostrarFormulario) return null;

    return (
        <Paper 
            elevation={0} 
            sx={{ 
                p: 3, 
                borderRadius: '24px', 
                bgcolor: PALETTE.pearl, 
                border: `1px solid ${PALETTE.ice}`,
                mt: 2,
                animation: 'fadeIn 0.3s ease-in-out'
            }}
        >
            <Stack spacing={2.5}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Printer size={20} color={PALETTE.teal} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: PALETTE.dark }}>
                        Detalles de Impresión
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>TIPO DE TRABAJO</Typography>
                        <TextField 
                            fullWidth 
                            name="tipo" 
                            value={formImpresion.tipo} 
                            onChange={handleChange} 
                            placeholder="Ej: Folletos, Posters, Libretas"
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>FECHA LÍMITE</Typography>
                        <TextField 
                            fullWidth 
                            type="date" 
                            name="fechaLimite" 
                            value={formImpresion.fechaLimite} 
                            onChange={handleChange} 
                            inputProps={{ min: new Date().toISOString().split('T')[0] }}
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>CANTIDAD</Typography>
                        <TextField 
                            fullWidth 
                            type="number" 
                            name="cantidadCopias" 
                            value={formImpresion.cantidadCopias} 
                            onChange={handleChange}
                            InputProps={{ startAdornment: <Hash size={16} style={{marginRight: 8, color: PALETTE.slate}} /> }}
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>GRAMAJE</Typography>
                        <TextField 
                            fullWidth 
                            name="gramaje" 
                            value={formImpresion.gramaje} 
                            onChange={handleChange} 
                            placeholder="Ej:80"
                            InputProps={{ startAdornment: <Layers size={16} style={{marginRight: 8, color: PALETTE.slate}} /> }}
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>TAMAÑO</Typography>
                        <TextField 
                            fullWidth 
                            name="tamanio" 
                            value={formImpresion.tamanio} 
                            onChange={handleChange} 
                            placeholder="Ej: A4, 10x15"
                            InputProps={{ startAdornment: <Maximize size={16} style={{marginRight: 8, color: PALETTE.slate}} /> }}
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: PALETTE.slate }}>PRESUPUESTO ESTIMADO</Typography>
                        <TextField 
                            fullWidth 
                            type="number" 
                            name="presupuesto" 
                            value={formImpresion.presupuesto} 
                            onChange={handleChange}
                            InputProps={{ startAdornment: <DollarSign size={16} style={{marginRight: 8, color: PALETTE.slate}} /> }}
                            sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl component="fieldset" sx={{ mt: 1, ml: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: PALETTE.slate, mb: 1, display: 'block' }}>¿LLEVA PLASTIFICADO?</Typography>
                            <RadioGroup 
                                row 
                                name="plastificado" 
                                value={formImpresion.plastificado.toString()} 
                                onChange={handleChange}
                            >
                                <FormControlLabel value="true" control={<Radio sx={{ color: PALETTE.teal, '&.Mui-checked': { color: PALETTE.teal } }} />} label={<Typography variant="body2" fontWeight={600}>Sí</Typography>} />
                                <FormControlLabel value="false" control={<Radio sx={{ color: PALETTE.teal, '&.Mui-checked': { color: PALETTE.teal } }} />} label={<Typography variant="body2" fontWeight={600}>No</Typography>} />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                </Grid>

                <Button 
                    variant="contained" 
                    fullWidth 
                    onClick={agregarTareaAlPedido}
                    startIcon={<PlusCircle size={18} />}
                    sx={{ 
                        mt: 1, 
                        borderRadius: '12px', 
                        py: 1.2, 
                        fontWeight: 800, 
                        bgcolor: PALETTE.teal,
                        '&:hover': { bgcolor: PALETTE.dark }
                    }}
                >
                    Añadir Impresión al Pedido
                </Button>
            </Stack>
        </Paper>
    );
};

export default Impresion;